import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { miDia } from '../datos/consultas.js'
import { encolar } from '../datos/sync.js'
import { fechaLarga, hora, hoy, sumarDias } from '../datos/formato.js'
import { db } from '../datos/db.js'

/**
 * Vista del profesional (T07): "Mi día" como lista, no como grilla.
 *
 * Usable a una mano en 375 px. **Sin funciones de recepción** (§7.1): no
 * agenda, no mueve, no cancela. Marca lo que pasa con el paciente que tiene
 * enfrente, que es lo unico que el profesional hace con el telefono en la mano.
 */
export function MiDia({
  recursoPersonaId,
  onAtencion,
}: {
  recursoPersonaId: string
  onAtencion: (citaId: string) => void
}) {
  const [dia, setDia] = useState(hoy())
  const citas = useLiveQuery(() => miDia(recursoPersonaId, dia), [recursoPersonaId, dia], [])

  async function marcar(citaId: string, estado: 'no_asistio') {
    // Optimista: se escribe en Dexie y se encola. La UI ya muestra el cambio
    // aunque no haya red, que es todo el punto del offline.
    await db.cita.update(citaId, { estado })
    await encolar('cambiar_estado', { citaId, estado })
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => setDia(sumarDias(dia, -1))}
          className="rounded-md px-3 py-2 text-lg text-slate-600"
          aria-label="Día anterior"
        >
          ‹
        </button>
        <h1 className="flex-1 text-center text-sm font-medium text-slate-800 first-letter:uppercase">
          {fechaLarga(`${dia}T12:00:00Z`)}
        </h1>
        <button
          type="button"
          onClick={() => setDia(sumarDias(dia, 1))}
          className="rounded-md px-3 py-2 text-lg text-slate-600"
          aria-label="Día siguiente"
        >
          ›
        </button>
      </header>

      {citas.length === 0 ? (
        <p className="p-8 text-center text-slate-500">No tenés citas este día.</p>
      ) : (
        <ol className="divide-y divide-slate-100">
          {citas.map(({ tramo, cita, cliente }) => (
            <li key={tramo.id} className="p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-lg font-semibold tabular-nums text-slate-900">
                  {hora(tramo.inicio)}
                </span>
                <span className="text-sm text-slate-500">
                  {hora(tramo.inicio)}–{hora(tramo.fin)}
                </span>
              </div>
              <p className="mt-1 text-base text-slate-800">{cliente?.nombre ?? 'Sin cliente'}</p>
              {cita.notas && <p className="mt-1 text-sm text-slate-500">{cita.notas}</p>}

              <EstadoCita estado={cita.estado} />

              {/* Botones grandes: se usan a una mano, de pie, con guantes. */}
              <div className="mt-3 flex gap-2">
                {cita.estado !== 'atendida' && (
                  <button
                    type="button"
                    onClick={() => onAtencion(cita.id)}
                    className="min-h-11 flex-1 rounded-lg bg-teal-700 px-3 text-sm font-medium text-white"
                  >
                    Atender
                  </button>
                )}
                {cita.estado !== 'atendida' && (
                  <button
                    type="button"
                    onClick={() => void marcar(cita.id, 'no_asistio')}
                    className="min-h-11 flex-1 rounded-lg border border-slate-300 px-3 text-sm text-slate-700"
                  >
                    No asistió
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

function EstadoCita({ estado }: { estado: string }) {
  const etiquetas: Record<string, string> = {
    pendiente: 'Pendiente',
    confirmada: 'Confirmada',
    en_atencion: 'En atención',
    atendida: 'Atendida',
    no_asistio: 'No asistió',
  }
  const colores: Record<string, string> = {
    en_atencion: 'bg-amber-100 text-amber-900',
    atendida: 'bg-teal-100 text-teal-900',
    no_asistio: 'bg-slate-100 text-slate-600',
  }
  return (
    <span
      className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs ${colores[estado] ?? 'bg-slate-100 text-slate-600'}`}
    >
      {etiquetas[estado] ?? estado}
    </span>
  )
}
